import type { FsFileInput, FsFolderInput, FsNodeInput } from '@/fs/types';
import { playlist, playlistFileName, trackFileName } from './media';
import { site } from './site';

/**
 * The whole virtual filesystem, authored by hand — bar My Music, which is
 * generated from the playlist just below. `buildTree()` derives every `path`
 * and `ext` from this, so never write those here.
 *
 * Everything the user can browse lives in this one tree: the desktop icons are
 * literally the children of the Desktop folder, Explorer walks it, Notepad
 * opens from it, and Start-menu entries point into it by path.
 */

const USER = site.accountName;
export const USER_PROFILE = `C:\\Documents and Settings\\${USER}`;
export const DESKTOP_PATH = `${USER_PROFILE}\\Desktop`;
export const MY_DOCUMENTS_PATH = `${USER_PROFILE}\\My Documents`;
export const MY_PICTURES_PATH = `${MY_DOCUMENTS_PATH}\\My Pictures`;
export const MY_MUSIC_PATH = `${MY_DOCUMENTS_PATH}\\My Music`;
export const DRIVE_PATH = 'C:';
export const MY_COMPUTER_PATH = 'My Computer';

/**
 * My Music, taken straight off `config/media.ts` rather than typed out again.
 *
 * These are sound files, not shortcuts: each one names a track, and the bytes
 * behind it live in `public/assets/media/`. That is what makes the folder the
 * library rather than a picture of one — the players build their playlists by
 * listing these nodes (see `src/fs/media.ts`), so a track deleted here is a
 * track Winamp no longer has.
 *
 * Generating them keeps the folder and the playlist in the same order with the
 * same names — add a track to the playlist and it appears in the folder, which
 * is the direction the real thing worked too.
 */
const musicFiles: FsNodeInput[] = playlist.map((track) => ({
  type: 'file',
  name: trackFileName(track),
  comment: track.album ? `${track.artist} - ${track.album}` : track.artist,
  data: { kind: 'audio', trackId: track.id },
}));

/**
 * And the playlist that indexes them — a real file sitting beside the tracks,
 * the way Winamp left one behind. Its contents are written from whatever
 * sound files are in its folder *at the moment something asks*; see
 * `readText()` in `src/fs/useFs.ts`. Delete a track and the `.m3u` stops
 * naming it, exactly as re-saving the playlist would have.
 */
const playlistFile: FsFileInput = {
  type: 'file',
  name: playlistFileName,
  data: { kind: 'playlist' },
};

export const fsConfig: FsFolderInput = {
  type: 'folder',
  name: 'C:',
  special: 'drive',
  icon: '/assets/icons/drive.svg',
  comment: 'Local Disk (C:)',
  children: [
    {
      type: 'folder',
      name: 'Documents and Settings',
      children: [
        {
          type: 'folder',
          name: USER,
          children: [
            {
              type: 'folder',
              name: 'Desktop',
              special: 'desktop',
              children: [
                {
                  type: 'file',
                  name: 'My Documents.lnk',
                  icon: '/assets/icons/my-documents.svg',
                  comment: 'Opens your documents folder',
                  data: { kind: 'app', appId: 'explorer', args: { path: MY_DOCUMENTS_PATH } },
                },
                {
                  type: 'file',
                  name: 'My Computer.lnk',
                  icon: '/assets/icons/my-computer.svg',
                  comment: 'Browse the whole machine',
                  data: { kind: 'app', appId: 'explorer', args: { path: MY_COMPUTER_PATH } },
                },
                {
                  // Not a folder, despite what XP called it: there is no path
                  // to browse to, only the store in `src/recycle`. The window
                  // it opens is Explorer's chrome around that store.
                  type: 'file',
                  name: 'Recycle Bin',
                  icon: '/assets/icons/recycle-bin.svg',
                  comment: 'Contains the files and folders that you have deleted',
                  data: { kind: 'app', appId: 'recyclebin' },
                },
                {
                  type: 'file',
                  name: 'Internet Explorer.lnk',
                  icon: '/assets/icons/ie.svg',
                  comment: 'Browse the rest of teon.io',
                  data: { kind: 'app', appId: 'ie' },
                },
                {
                  type: 'file',
                  name: 'About me.txt',
                  comment: 'A brief introduction to me',
                  data: { kind: 'markdown', src: 'about.md' },
                },
                {
                  type: 'file',
                  name: 'Blog.url',
                  comment: 'https://blog.teon.io',
                  data: { kind: 'url', href: 'https://blog.teon.io' },
                },
                {
                  type: 'file',
                  name: 'Projects.url',
                  comment: 'https://projects.teon.io',
                  data: { kind: 'url', href: 'https://projects.teon.io' },
                },
                {
                  type: 'file',
                  name: 'Winamp.lnk',
                  icon: '/assets/icons/winamp.svg',
                  comment: "It really whips the llama's ass",
                  data: { kind: 'app', appId: 'winamp' },
                },
                {
                  type: 'file',
                  name: 'SkiFall.lnk',
                  icon: '/assets/icons/skifall.svg',
                  comment: 'Ski down the slope. A snow monster appears past 2000m',
                  data: { kind: 'app', appId: 'skifall' },
                },
                {
                  type: 'file',
                  name: 'Display Properties.lnk',
                  icon: '/assets/icons/display.svg',
                  comment: 'Change the wallpaper and colour scheme',
                  data: { kind: 'app', appId: 'display' },
                },
              ],
            },
            {
              type: 'folder',
              name: 'My Documents',
              special: 'my-documents',
              icon: '/assets/icons/my-documents.svg',
              children: [
                {
                  type: 'folder',
                  name: 'My Pictures',
                  special: 'my-pictures',
                  icon: '/assets/icons/my-pictures.svg',
                  children: [
                    // The six wallpapers, openable in Paint. They are JPEG and
                    // WebP on disk and `.bmp` in the fiction; the extension is
                    // what decides the icon and which program claims them.
                    {
                      type: 'file',
                      name: 'Bliss.bmp',
                      comment: 'The default wallpaper',
                      data: { kind: 'image', src: '/assets/wallpapers/bliss.webp' },
                    },
                    {
                      type: 'file',
                      name: 'Autumn.bmp',
                      data: { kind: 'image', src: '/assets/wallpapers/autumn.jpg' },
                    },
                    {
                      type: 'file',
                      name: 'Follow.bmp',
                      data: { kind: 'image', src: '/assets/wallpapers/follow.jpg' },
                    },
                    {
                      type: 'file',
                      name: 'Moon Flower.bmp',
                      data: { kind: 'image', src: '/assets/wallpapers/moon-flower.jpg' },
                    },
                    {
                      type: 'file',
                      name: 'Tulips.bmp',
                      data: { kind: 'image', src: '/assets/wallpapers/tulips.jpg' },
                    },
                    {
                      type: 'file',
                      name: 'Wind.bmp',
                      data: { kind: 'image', src: '/assets/wallpapers/wind.jpg' },
                    },
                    {
                      type: 'file',
                      name: 'Bliss.url',
                      comment: 'The hill, as it looks today',
                      data: { kind: 'url', href: 'https://en.wikipedia.org/wiki/Bliss_(image)' },
                    },
                  ],
                },
                {
                  type: 'folder',
                  name: 'My Music',
                  icon: '/assets/icons/media.svg',
                  children: [...musicFiles, playlistFile],
                },
                {
                  type: 'file',
                  name: 'About me.txt',
                  data: { kind: 'markdown', src: 'about.md' },
                },
                {
                  type: 'file',
                  name: 'Colophon.txt',
                  comment: 'How this desktop was built, and who it owes.',
                  data: { kind: 'markdown', src: 'colophon.md' },
                },
                {
                  type: 'file',
                  name: 'My sites.txt',
                  comment: 'TODO: write a description',
                  data: { kind: 'markdown', src: 'links.md' },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      type: 'folder',
      name: 'Program Files',
      special: 'program-files',
      children: [
        {
          type: 'folder',
          name: 'Internet Explorer',
          children: [
            {
              type: 'file',
              name: 'iexplore.lnk',
              icon: '/assets/icons/ie.svg',
              data: { kind: 'app', appId: 'ie' },
            },
          ],
        },
        {
          type: 'folder',
          name: 'Outlook Express',
          children: [
            {
              type: 'file',
              name: 'msimn.lnk',
              icon: '/assets/icons/outlook.svg',
              data: { kind: 'app', appId: 'outlook' },
            },
          ],
        },
        {
          type: 'folder',
          name: 'Windows Media Player',
          children: [
            {
              type: 'file',
              name: 'wmplayer.lnk',
              icon: '/assets/icons/mediaplayer.svg',
              data: { kind: 'app', appId: 'mediaplayer' },
            },
          ],
        },
        {
          type: 'folder',
          name: 'Winamp',
          children: [
            {
              type: 'file',
              name: 'winamp.lnk',
              icon: '/assets/icons/winamp.svg',
              data: { kind: 'app', appId: 'winamp' },
            },
            {
              type: 'folder',
              name: 'Plugins',
              children: [
                {
                  type: 'file',
                  name: 'vis_warp.lnk',
                  icon: '/assets/icons/visualizer.svg',
                  comment: 'Visualisation plugin — needs Winamp playing',
                  data: { kind: 'app', appId: 'visualizer' },
                },
              ],
            },
          ],
        },
        {
          type: 'folder',
          name: 'Accessories',
          children: [
            {
              type: 'file',
              name: 'notepad.lnk',
              icon: '/assets/icons/notepad.svg',
              data: { kind: 'app', appId: 'notepad' },
            },
            {
              type: 'file',
              name: 'mspaint.lnk',
              icon: '/assets/icons/paint.svg',
              data: { kind: 'app', appId: 'paint' },
            },
            {
              type: 'file',
              name: 'cmd.lnk',
              icon: '/assets/icons/cmd.svg',
              data: { kind: 'app', appId: 'cmd' },
            },
            {
              type: 'file',
              name: 'calc.lnk',
              icon: '/assets/icons/calc.svg',
              data: { kind: 'app', appId: 'calc' },
            },
          ],
        },
        {
          type: 'folder',
          name: 'Games',
          children: [
            {
              type: 'file',
              name: 'winmine.lnk',
              icon: '/assets/icons/minesweeper.svg',
              comment: 'Minesweeper',
              data: { kind: 'app', appId: 'minesweeper' },
            },
            {
              type: 'file',
              name: 'sol.lnk',
              icon: '/assets/icons/solitaire.svg',
              comment: 'Solitaire',
              data: { kind: 'app', appId: 'solitaire' },
            },
            {
              type: 'file',
              name: 'ski32.lnk',
              icon: '/assets/icons/skifall.svg',
              comment: 'SkiFall',
              data: { kind: 'app', appId: 'skifall' },
            },
          ],
        },
      ],
    },
    {
      type: 'folder',
      name: 'WINDOWS',
      children: [
        {
          type: 'file',
          name: 'README.txt',
          comment: 'Please read before making changes',
          data: { kind: 'text', src: 'readme.txt' },
        },
        {
          type: 'folder',
          name: 'system32',
          children: [
            {
              type: 'file',
              name: 'notepad.lnk',
              icon: '/assets/icons/notepad.svg',
              data: { kind: 'app', appId: 'notepad' },
            },
            {
              type: 'file',
              name: 'cmd.lnk',
              icon: '/assets/icons/cmd.svg',
              data: { kind: 'app', appId: 'cmd' },
            },
            {
              type: 'file',
              name: 'mspaint.lnk',
              icon: '/assets/icons/paint.svg',
              data: { kind: 'app', appId: 'paint' },
            },
          ],
        },
      ],
    },
  ],
};
